/**
 * Asset detail sheet for the AI inventory.
 *
 * Renders a discovered asset's detail and a gated "run security scan" CTA
 * driven by the backend `scan_capability`:
 *   - eligible      -> navigate to the existing LLM scan creation flow
 *                      (/compliance), prefilled with the SageMaker endpoint.
 *   - tier_upgrade_required -> open the Stripe-checkout upgrade dialog.
 *   - unsupported_asset_type -> show "not yet scannable".
 *
 * SECURITY: every asset string (name, arn, metadata values, posture messages)
 * comes from a customer's AWS account and may be attacker-controlled. It is
 * rendered as text only — never as HTML, never via dangerouslySetInnerHTML.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Loader2, Lock, ScanSearch } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AwsDiscoveryUpgradeDialog } from "./AwsDiscoveryUpgradeDialog";
import { PostureFlagBadge } from "./awsDiscoveryShared";
import { truncate } from "./awsDiscoveryFormat";
import { useAwsDiscoveryAsset } from "@/hooks/useAwsDiscovery";
import {
  ASSET_TYPE_LABELS,
  postureFlagLabel,
  postureFlagSeverity,
} from "@/types/awsDiscovery";

interface AwsDiscoveryAssetDetailSheetProps {
  assetId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AwsDiscoveryAssetDetailSheet({
  assetId,
  open,
  onOpenChange,
}: AwsDiscoveryAssetDetailSheetProps) {
  const navigate = useNavigate();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { data: asset, isLoading, isError, error } = useAwsDiscoveryAsset(open ? assetId : null);

  const handleScanCta = () => {
    if (!asset) return;
    const capability = asset.scan_capability;

    if (capability.eligible && asset.asset_type === "sagemaker_endpoint") {
      // Hand off to the existing LLM scan creation flow, prefilled with this
      // SageMaker endpoint. The scan flow lives at /compliance.
      navigate("/compliance", {
        state: {
          prefill: {
            source: "shadow-ai-discovery",
            assetId: asset.id,
            assetType: asset.asset_type,
            endpointName: asset.name,
            arn: asset.arn_or_native_id,
            region: asset.region,
          },
        },
      });
      onOpenChange(false);
      return;
    }

    if (capability.reason === "tier_upgrade_required") {
      setUpgradeOpen(true);
    }
  };

  const renderCta = () => {
    if (!asset) return null;
    const capability = asset.scan_capability;

    // Only SAGEMAKER_ENDPOINT is directly scannable in v1.
    if (capability.reason === "unsupported_asset_type" || asset.asset_type !== "sagemaker_endpoint") {
      return (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
          This asset type isn't directly scannable yet. In v1, security scans run against
          SageMaker endpoints.
        </div>
      );
    }

    if (capability.reason === "tier_upgrade_required") {
      return (
        <Button className="w-full" onClick={handleScanCta}>
          <Lock className="h-4 w-4 mr-2" />
          Upgrade to run security scan
        </Button>
      );
    }

    if (capability.eligible) {
      return (
        <Button className="w-full" onClick={handleScanCta}>
          <ScanSearch className="h-4 w-4 mr-2" />
          Run security scan
        </Button>
      );
    }

    return (
      <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
        This endpoint isn't eligible for a scan right now.
      </div>
    );
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {isLoading && (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading asset…
            </div>
          )}

          {isError && (
            <div className="flex items-start gap-2 py-8 text-sm text-destructive">
              <ShieldAlert className="h-4 w-4 mt-0.5" />
              {error?.message || "Failed to load asset detail."}
            </div>
          )}

          {asset && (
            <>
              <SheetHeader>
                <Badge variant="outline" className="w-fit text-xs">
                  {ASSET_TYPE_LABELS[asset.asset_type] ?? asset.asset_type}
                </Badge>
                {/* Text rendering only — untrusted customer string. */}
                <SheetTitle className="break-all">{truncate(asset.name, 200)}</SheetTitle>
                <SheetDescription className="break-all font-mono text-xs">
                  {truncate(asset.arn_or_native_id, 300)}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Quick facts */}
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      Region
                    </dt>
                    <dd className="font-mono">{truncate(asset.region, 40)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      Status
                    </dt>
                    <dd>{asset.is_active ? "Active" : "Inactive"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      First seen
                    </dt>
                    <dd>{new Date(asset.first_seen_at).toLocaleString()}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      Last seen
                    </dt>
                    <dd>{new Date(asset.last_seen_at).toLocaleString()}</dd>
                  </div>
                </dl>

                <Separator />

                {/* Posture flags */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Posture flags</h3>
                  {asset.posture_flags.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No posture flags.</p>
                  ) : (
                    <ul className="space-y-2">
                      {asset.posture_flags.map((code, idx) => (
                        <li
                          key={`${code}-${idx}`}
                          className="rounded-md border p-3 text-sm"
                        >
                          <div className="flex items-center gap-2">
                            <PostureFlagBadge severity={postureFlagSeverity(code)} />
                            {/* Untrusted code string — text only + truncated. */}
                            <span className="text-sm">{postureFlagLabel(truncate(code, 60))}</span>
                          </div>
                          <p className="mt-1 font-mono text-xs text-muted-foreground">
                            {truncate(code, 80)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Metadata */}
                {asset.asset_metadata && metadataEntries(asset.asset_metadata).length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium">Metadata</h3>
                      <dl className="space-y-1 text-sm">
                        {metadataEntries(asset.asset_metadata).map(([key, value]) => (
                          <div key={key} className="grid grid-cols-3 gap-2">
                            <dt className="col-span-1 truncate text-xs uppercase tracking-wide text-muted-foreground">
                              {truncate(key, 40)}
                            </dt>
                            <dd className="col-span-2 break-all font-mono text-xs">
                              {truncate(formatMetadataValue(value), 300)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </>
                )}

                <Separator />

                {/* Gated CTA */}
                <div className="space-y-2">{renderCta()}</div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AwsDiscoveryUpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        assetName={asset?.name}
      />
    </>
  );
}

/**
 * Metadata keys that must never be surfaced. Env var VALUES (and any hash/representation
 * of them) are secret-derived fingerprints, so the backend no longer emits them. This
 * filter is belt-and-suspenders: even a stale record never renders such a key.
 */
const HIDDEN_METADATA_KEYS = new Set(["matched_env_value_hashes"]);

/**
 * Visible metadata entries: drops keys that must never be displayed.
 */
function metadataEntries(
  metadata: Record<string, unknown>,
): Array<[string, unknown]> {
  return Object.entries(metadata).filter(([key]) => !HIDDEN_METADATA_KEYS.has(key));
}

/**
 * Render a metadata value as a plain string. Objects/arrays are JSON-encoded.
 * Result is always rendered as text by the caller.
 */
function formatMetadataValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return "[unserializable]";
  }
}
